import { Button, Card, Checkbox, Grid, Modal, MultiSelect, NumberInput, Select, Text } from '@mantine/core';
import { IconCopy, IconPencil, IconPlus } from '@tabler/icons-react';
import ISO6391 from 'iso-639-1';
import { useState } from 'react';
import prettifiedLabel from '../constants/labelPrettifier';
import DisplayModePicker from './DisplayModePicker';
import SearchableCombobox from './SearchableCombobox';
import TemplatesDetailsForm from './Templates/TemplatesDetailsForm';
import TemplatesNewForm from './Templates/TemplatesNewForm';
import URLFilterConfig from './UrlFilterConfig';

// When and whether the block is shown. How it looks — the chrome the SDK draws
// around it, and the preview — is its sibling `PageBlockAppearance`, on a tab
// of its own. Display mode stays here rather than there: it is the control that
// decides what this form offers at all (a `static` block has no trigger), so an
// author changing it has to be able to see the rules it changes.
//
const PageBlockDisplayConfiguration = ({
  pageBlock,
  templates,
  template,
  handleChange,
  // Whether the leave-intent Back-button fallback is offered. Banner blocks
  // pass true; recommenders leave it off, as the field is not on their API
  // model and their trigger never reaches the SDK on the live push path.
  withLeaveIntentBackButton = false,
  refreshTemplates = () => {},
}) => {
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [showNewTemplateModal, setShowNewTemplateModal] = useState(false);

  const triggerOptions = [
    { label: 'Show immediately', value: 'immediately' },
    { label: 'On scrolling down N% of page', value: 'scrollPercentage' },
    { label: 'After N seconds on the page', value: 'delaySeconds' },
    { label: 'On leave intent', value: 'leaveIntent' },
    { label: 'Added to Wishlist', value: 'wishlistChanged' },
    { label: 'Added to Cart', value: 'cartChanged' },
  ];

  const codes = ISO6391.getLanguages(ISO6391.getAllCodes());

  const localesOptions = codes.map((locale) => ({
    label: locale.name,
    value: locale.code,
  }));

  const handleLocalesChange = (value) => {
    handleChange({ target: { name: 'storeLocales', value } });
  };

  const templateOptions = templates
    ? templates.map((t) => {
        return {
          label: t.name,
          value: t.id.toString(),
        };
      })
    : [];

  return (
    <Card withBorder>
      <Card.Section p="md">
        {templateOptions.length > 0 && (
          <Grid mb="md" data-tour="templateField">
            <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Layout</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 5 }}>
              <SearchableCombobox
                clearable={true}
                options={[...templateOptions.filter((opt) => opt.label), { label: '(none)', value: '' }]}
                value={pageBlock.templateId?.toString() || ''}
                onChange={(value) => handleChange({ target: { name: 'templateId', value: value || null } })}
                placeholder="Select layout"
                searchPlaceholder="Search layouts..."
                emptyMessage="No layouts found"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 2 }}>
              <Button fullWidth onClick={() => setShowEditTemplateModal(true)} disabled={!pageBlock.templateId}>
                <IconPencil size={16} /> Edit
              </Button>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 2 }}>
              <Button fullWidth onClick={() => setShowNewTemplateModal(true)}>
                {pageBlock.templateId ? <IconCopy size={16} /> : <IconPlus size={16} />}{' '}
                {pageBlock.templateId ? 'Clone & Edit' : 'New'}
              </Button>
            </Grid.Col>
          </Grid>
        )}

        <>
          <Grid mb="md" data-tour="blockDisplayModeSelect">
            <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Display mode</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <DisplayModePicker
                value={pageBlock.displayType ?? ''}
                onChange={(value) => handleChange({ target: { name: 'displayType', value } })}
              />
            </Grid.Col>
          </Grid>
        </>
        {pageBlock.displayType !== 'static' && (
          <Grid mb="md" data-tour="triggerSelect">
            <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Trigger</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <Select
                data={triggerOptions}
                onChange={(value) => handleChange({ target: { name: 'trigger', value } })}
                value={pageBlock.trigger}
              />
            </Grid.Col>
          </Grid>
        )}

        {pageBlock.trigger === 'delaySeconds' && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Show After Seconds</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <NumberInput
                name="delaySeconds"
                min={0}
                step={1}
                value={pageBlock.delaySeconds || ''}
                placeholder="e.g. 5"
                onChange={(value) => handleChange({ target: { name: 'delaySeconds', value } })}
              />
              <Text size="xs" c="dimmed" mt="xs">
                Enter the number of seconds the user has to stay on the page to see the pageBlock.
              </Text>
            </Grid.Col>
          </Grid>
        )}
        {pageBlock.trigger === 'scrollPercentage' && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Show After % scroll</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <NumberInput
                name="scrollPercentage"
                min={0}
                max={100}
                step={1}
                value={pageBlock.scrollPercentage || ''}
                placeholder="e.g. 20"
                onChange={(value) => handleChange({ target: { name: 'scrollPercentage', value } })}
              />
              <Text size="xs" c="dimmed" mt="xs">
                Enter the percent of the page the user has to scroll to before the template is displayed.
              </Text>
            </Grid.Col>
          </Grid>
        )}
        {withLeaveIntentBackButton && pageBlock.trigger === 'leaveIntent' && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Mobile Back button</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <Checkbox
                label="Also trigger on mobile Back button"
                checked={!!pageBlock.leaveIntentBackButton}
                onChange={(e) =>
                  handleChange({ target: { name: 'leaveIntentBackButton', value: e.currentTarget.checked } })
                }
              />
              <Text size="xs" c="dimmed" mt="xs">
                Leave intent is a mouse movement, so it never fires on a phone. This fallback shows the block on the
                first Back press instead. It only works on the page the visitor landed on, only after they have
                interacted with that page, and a second Back press leaves the site. It does not work in in-app browsers
                such as Instagram or TikTok, nor with the iOS swipe-back gesture.
              </Text>
            </Grid.Col>
          </Grid>
        )}
        <URLFilterConfig
          labelCol={3}
          selectorCol={9}
          modelInstance={pageBlock}
          modelName="pageBlock"
          handleChange={handleChange}
        />
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Locales to Display</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <MultiSelect
              data={localesOptions}
              onChange={handleLocalesChange}
              value={pageBlock?.storeLocales || []}
              placeholder="Type to search for locale..."
            />
            <Text size="xs" c="dimmed" mt="xs">
              Specify multiple locales to restrict display to those specific locales. If left empty, it will display in
              all locales.
            </Text>
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Session Interval</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <NumberInput
              value={pageBlock.sessionInterval ?? ''}
              placeholder="Every session"
              min={1}
              allowDecimal={false}
              allowNegative={false}
              onChange={(value) =>
                handleChange({ target: { name: 'sessionInterval', value: value === '' ? null : value } })
              }
            />
            <Text size="xs" c="dimmed" mt="xs">
              Show this banner every N sessions. Leave empty to show every session.
            </Text>
          </Grid.Col>
        </Grid>

        {template && (
          <Modal
            opened={showEditTemplateModal}
            onClose={() => setShowEditTemplateModal(false)}
            size="full"
            styles={{ content: { width: '100%', maxWidth: '100%' } }}
            title="Edit Layout"
          >
            <TemplatesDetailsForm
              template={template}
              onSuccess={() => {
                refreshTemplates(() => {
                  setShowEditTemplateModal(false);
                });
              }}
              onBack={() => setShowEditTemplateModal(false)}
            />
          </Modal>
        )}

        <Modal
          opened={showNewTemplateModal}
          onClose={() => setShowNewTemplateModal(false)}
          size="full"
          styles={{ content: { width: '100%', maxWidth: '100%' } }}
          title="New Layout"
        >
          <TemplatesNewForm
            from={template?.id}
            type={prettifiedLabel(template?.scope || 'template')}
            onSuccess={(template) => {
              refreshTemplates(() => {
                handleChange({ target: { name: 'templateId', value: template.id } });
                setShowNewTemplateModal(false);
              });
            }}
            onBack={() => setShowNewTemplateModal(false)}
          />
        </Modal>
      </Card.Section>
    </Card>
  );
};

export default PageBlockDisplayConfiguration;
